import AttachFileIcon from '@mui/icons-material/AttachFile';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { ChangeEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { HcoIcon } from '@/assets/icons/HcoIcon';
import { HcpIcon } from '@/assets/icons/HcpIcon';
import { StatusChip } from '@/components/Chips/StatusChip/StatusChip';
import { ProfileName } from '@/components/Hcp/ProfileName/ProfileName';
import { Layout } from '@/components/Layout/Layout';
import { NoSearchResults } from '@/components/NoSearchResults/NoSearchResults';
import { SearchBar } from '@/components/SearchBar/SearchBar';
import { SectionBlock } from '@/components/SectionBlock/SectionBlock';
import { DEFAULT_PAGE_SIZE } from '@/core/constants/app';
import { Dcr, DcrEntityType, DcrType } from '@/core/graphql/generated/types';
import { ViewDCRModal } from '@/pages/MyDCRsPage/ViewDCRModal/ViewDCRModal';
import { formatDate } from '@/utils/formatters/formatData';

import { useDcrQueryParams } from './_hooks/useDcrQueryParams';
import { useMyDcrs } from './_hooks/useMyDcrs';
import { computeDcrStatus } from './_utils/computeDcrStatus';
import { processEntityData } from './_utils/processEntityData';
import { AddMissingProfileMenu } from './AddMissingProfileMenu/AddMissingProfileMenu';
import { viewDcrsPageStyles as sx } from './MyDCRsPage.styles';
import { NoDCRsOverlay } from './NoDCRsOverlay/NoDCRsOverlay';
import { StatusFilter } from './StatusFilter/StatusFilter';

type Props = {
	statusFilterVariant?: 'default' | 'chip';
};

const NullOverlay = () => null;

export const MyDCRsPage = ({ statusFilterVariant = 'chip' }: Props = {}) => {
	const { t } = useTranslation();
	const {
		dcrSearch,
		dcrStatuses,
		dcrPage,
		setDcrSearch,
		setDcrStatuses,
		setDcrPage
	} = useDcrQueryParams();
	const [open, setOpen] = useState(false);
	const {
		dcrs,
		loading,
		isInitialLoading,
		hasNoDcrs,
		hasSettledActiveFilters,
		debouncedSearchTerm,
		statusFilterLoading,
		rowCount,
		activeDcr,
		setActiveDcrId,
		paginationModel,
		facetCounts
	} = useMyDcrs(dcrSearch, dcrStatuses, dcrPage);

	const columns: GridColDef[] = useMemo(
		() => [
			{
				field: 'name',
				headerName: t('Name'),
				minWidth: 200,
				flex: 0.3,
				renderCell: ({ row }: { row: Dcr }) => (
					<Box sx={sx.nameCell}>
						{row.entityType === DcrEntityType.Hcp ? (
							<HcpIcon fill='var(--mui-palette-primary-main)' />
						) : (
							<HcoIcon fill='var(--mui-palette-primary-main)' />
						)}

						{row.type === DcrType.Change ? (
							<ProfileName
								profile={row.currentEntity ?? null}
								variant='subtitle1'
								withInactive
								inline
								withInternationalName
							/>
						) : (
							<ProfileName
								profile={
									row.entityData
										? (processEntityData(row.entityData, row.entityType) ??
											null)
										: null
								}
								variant='subtitle1'
								inline
								withInternationalName
							/>
						)}
					</Box>
				)
			},
			{
				field: 'type',
				headerName: t('DCR Type'),
				width: 150,
				minWidth: 120,
				flex: 0.1
			},
			{
				field: 'createdAt',
				headerName: t('Submitted'),
				type: 'date',
				width: 150,
				minWidth: 120,
				flex: 0.1,
				valueFormatter: value => {
					if (!value) return '-';
					return formatDate(value);
				}
			},
			{
				field: 'completedAt',
				headerName: t('Resolved'),
				type: 'date',
				width: 150,
				minWidth: 120,
				flex: 0.1,
				valueFormatter: value => {
					if (!value) return '-';
					return formatDate(value);
				}
			},
			{
				field: 'displayStatus',
				headerName: t('Status'),
				width: 200,
				minWidth: 200,
				renderCell: ({ row }) => <StatusChip status={computeDcrStatus(row)} />
			},
			{
				field: 'attachments',
				headerName: '',
				width: 48,
				minWidth: 48,
				sortable: false,
				filterable: false,
				disableColumnMenu: true,
				renderCell: ({ row }: { row: Dcr }) => {
					const hasAttachments = (row.attachments ?? []).length > 0;
					if (!hasAttachments) return null;

					return (
						<Box sx={sx.attachmentsCell} aria-label={t('Has attachments')}>
							<AttachFileIcon fontSize='small' />
						</Box>
					);
				}
			}
		],
		[t]
	);

	const handleClear = () => {
		setDcrSearch('');
	};

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		setDcrSearch(event.target.value);
	};

	const handleRowClick = ({ row }: { row: Dcr }) => {
		if (computeDcrStatus(row) !== 'processing') {
			setActiveDcrId(row.id);
			setOpen(true);
		}
	};

	const handleModalClose = () => {
		setOpen(false);
		setActiveDcrId(null);
	};

	const NoRowsOverlay = () => {
		if (loading || !hasNoDcrs) return null;

		return hasSettledActiveFilters ? (
			<NoSearchResults
				query={debouncedSearchTerm || undefined}
				withName
				withIcon
				withFiltersHint
				singleFilter
				displayBackButton={false}
			/>
		) : (
			<NoDCRsOverlay />
		);
	};

	return (
		<Layout title={t('My DCRs')} customSx={sx.layout}>
			<Box component='section' sx={sx.root}>
				<SectionBlock
					title={t('My DCRs')}
					controls={
						isInitialLoading ? (
							<Skeleton
								animation={false}
								variant='rounded'
								width={190}
								height={36}
							/>
						) : (
							<AddMissingProfileMenu />
						)
					}
					controlsAlwaysVisible
					hideHeader={!loading && !hasSettledActiveFilters && hasNoDcrs}
					customSx={sx.sectionBlockCustomSx(isInitialLoading)}
				>
					{(!hasNoDcrs || hasSettledActiveFilters || isInitialLoading) && (
						<Box sx={sx.filterBox}>
							<SearchBar
								placeholder={t('Search DCRs')}
								value={dcrSearch}
								handleChange={handleChange}
								handleClear={handleClear}
								customSx={sx.searchBar}
							/>
							<StatusFilter
								selectedValues={dcrStatuses}
								handleChange={setDcrStatuses}
								variant={statusFilterVariant}
								facetCounts={facetCounts}
								loading={statusFilterLoading}
							/>
						</Box>
					)}
					<DataGrid
						rows={dcrs ?? []}
						columns={columns}
						onRowClick={handleRowClick}
						sx={sx.datagrid}
						disableRowSelectionOnClick
						disableColumnFilter
						disableColumnSorting
						disableColumnMenu
						disableColumnResize
						getRowClassName={({ row }) =>
							computeDcrStatus(row) === 'processing' ? 'row-disabled' : ''
						}
						slots={{
							noRowsOverlay: NoRowsOverlay,
							noResultsOverlay: NoRowsOverlay,
							columnHeaders:
								loading || dcrs.length > 0 ? undefined : NullOverlay,
							footer: loading || dcrs.length > 0 ? undefined : NullOverlay
						}}
						slotProps={{
							loadingOverlay: {
								variant: 'skeleton',
								noRowsVariant: 'skeleton'
							}
						}}
						loading={loading}
						rowCount={rowCount}
						paginationModel={paginationModel}
						paginationMode='server'
						onPaginationModelChange={({ page }) => setDcrPage(page + 1)}
						pageSizeOptions={[DEFAULT_PAGE_SIZE]}
					/>
				</SectionBlock>
			</Box>

			<ViewDCRModal open={open} onClose={handleModalClose} dcr={activeDcr} />
		</Layout>
	);
};
